// Dashboard teaser for the daily game-wins leaderboard: top 5, the signed-in
// player's position, and a CTA to the full Leaderboard page.
// Design source: claude.ai design project "Circlechell leaderboard dashboard".

import React from 'react';
import {
  Pressable,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import CCText from '@/Atoms/CCText';
import {ScreenName} from '@/navigators/StackConstants';
import {logEvent} from '@/Utils/GAEvents';
import {
  WIDE_BREAKPOINT,
  lbColors,
  lbRadius,
  lbShadow,
} from '@/Screens/Leaderboard/leaderboardTheme';
import {useLeaderboardData} from '@/Screens/Leaderboard/useLeaderboardData';
import LiveBadge from '@/Screens/Leaderboard/components/LiveBadge';
import PlayerAvatar from '@/Screens/Leaderboard/components/PlayerAvatar';
import {
  SkeletonChip,
  SkeletonRows,
} from '@/Screens/Leaderboard/components/Skeleton';

const PREVIEW_LIMIT = 5;

function LeaderboardPreview() {
  const navigation = useNavigation();
  const {width} = useWindowDimensions();
  const wide = width >= WIDE_BREAKPOINT;
  const {rows, me, totalPlayers, isLoading, isError} = useLeaderboardData(
    'daily',
    {refetchIntervalMs: 60000},
  );
  const top = rows.slice(0, PREVIEW_LIMIT);

  const onViewAll = () => {
    logEvent('event', 'leaderboard_preview_view_all', 'dashboard');
    navigation.navigate(ScreenName.Leaderboard);
  };

  // Hide a broken teaser on error, or once loaded with no players — but while
  // loading, keep the card mounted and show skeletons so it doesn't pop in
  // after the rest of the dashboard.
  if (isError || (!isLoading && top.length === 0)) {
    return null;
  }

  return (
    <View style={[styles.card, lbShadow.card]}>
      {/* header */}
      <View style={styles.header}>
        <View style={styles.iconCircle}>
          <CCText fontSize={24}>🏆</CCText>
        </View>
        <View style={{flex: 1}}>
          <View style={styles.titleRow}>
            <CCText fontSize={21} fontWeight="bold" color={lbColors.ink}>
              Daily Leaderboard
            </CCText>
            <LiveBadge />
          </View>
          <CCText
            fontSize={13.5}
            fontWeight="semibold"
            color={lbColors.muted}
            style={{marginTop: 2}}>
            Top players by wins today · updates in real time
          </CCText>
        </View>
        {wide ? <ViewAllButton onPress={onViewAll} /> : null}
      </View>

      <View style={[styles.body, wide && styles.bodyWide]}>
        {/* top list */}
        <View style={[styles.topList, wide && {flex: 1.6}]}>
          {isLoading ? (
            <SkeletonRows count={PREVIEW_LIMIT} compact />
          ) : (
            top.map(r => (
            <View
              key={r.userid}
              style={[
                styles.row,
                {
                  backgroundColor: r.flash
                    ? lbColors.flashBg
                    : r.you
                    ? lbColors.youRowBg
                    : lbColors.rowBg,
                },
              ]}>
              <CCText
                fontSize={15}
                fontWeight="bold"
                color={r.medal ? r.medal.tx : lbColors.muted}
                style={styles.rank}>
                {r.rank}
              </CCText>
              <PlayerAvatar
                initials={r.initials}
                color={r.color}
                avatarUrl={r.avatarUrl}
                size={32}
              />
              <CCText
                fontSize={15}
                fontWeight="bold"
                color={lbColors.ink}
                showEllipses
                style={styles.name}>
                {r.name}
              </CCText>
              <View style={styles.winsWrap}>
                <CCText fontSize={18} fontWeight="bold" color={lbColors.ink}>
                  {r.wins}
                </CCText>
                <CCText
                  fontSize={12}
                  fontWeight="semibold"
                  color={lbColors.muted}>
                  {' wins'}
                </CCText>
              </View>
            </View>
            ))
          )}
        </View>

        {/* your position */}
        {isLoading ? (
          <View style={wide ? {flex: 1} : undefined}>
            <SkeletonChip />
          </View>
        ) : me ? (
          <View style={[styles.youChip, wide && {flex: 1}]}>
            <CCText
              fontSize={12}
              fontWeight="bold"
              color={lbColors.youChipLabel}
              style={styles.youLabel}>
              YOUR POSITION TODAY
            </CCText>
            <View style={styles.youBody}>
              <PlayerAvatar
                initials={me.initials}
                color={lbColors.teal}
                avatarUrl={me.avatarUrl}
                size={52}
              />
              <View style={{flex: 1}}>
                <CCText fontSize={30} fontWeight="bold" color={lbColors.ink}>
                  {me.rank != null ? `#${me.rank}` : '—'}
                </CCText>
                <CCText
                  fontSize={13}
                  fontWeight="semibold"
                  color={lbColors.youChipSub}
                  style={{marginTop: 3}}>
                  of {totalPlayers} players
                </CCText>
              </View>
              <View style={{alignItems: 'flex-end'}}>
                <CCText fontSize={26} fontWeight="bold" color={lbColors.goldInk}>
                  {me.wins}
                </CCText>
                <CCText
                  fontSize={12}
                  fontWeight="semibold"
                  color={lbColors.youChipLabel}>
                  wins today
                </CCText>
              </View>
            </View>
          </View>
        ) : null}
      </View>

      {!wide ? <ViewAllButton onPress={onViewAll} full /> : null}
    </View>
  );
}

function ViewAllButton({onPress, full}: {onPress: () => void; full?: boolean}) {
  return (
    <Pressable
      onPress={onPress}
      style={({pressed}) => [
        styles.cta,
        full && styles.ctaFull,
        pressed && {opacity: 0.85},
      ]}>
      <CCText fontSize={14} fontWeight="bold" color={lbColors.goldInk}>
        View full leaderboard
      </CCText>
      <CCText fontSize={16} fontWeight="bold" color={lbColors.goldInk}>
        →
      </CCText>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: lbColors.card,
    borderRadius: lbRadius.xl + 4,
    padding: 26,
    flexBasis: '100%',
    marginVertical: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  iconCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: lbColors.orange,
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleRow: {flexDirection: 'row', alignItems: 'center', gap: 10},
  body: {gap: 20},
  bodyWide: {flexDirection: 'row'},
  topList: {gap: 5},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: lbRadius.md,
  },
  rank: {width: 22, textAlign: 'center'},
  name: {flex: 1},
  winsWrap: {flexDirection: 'row', alignItems: 'baseline'},
  youChip: {
    backgroundColor: lbColors.youChipBg,
    borderRadius: lbRadius.lg + 2,
    padding: 22,
    justifyContent: 'center',
  },
  youLabel: {letterSpacing: 0.5, marginBottom: 14},
  youBody: {flexDirection: 'row', alignItems: 'center', gap: 14},
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: lbColors.gold,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: lbRadius.pill,
  },
  ctaFull: {marginTop: 14, justifyContent: 'center'},
});

export default LeaderboardPreview;
